import { ArrowSquareOut, ArrowDown, ArrowUp, Minus } from '@phosphor-icons/react';
import { RatingBadge } from '@/ui/RatingBadge';
import type {
  ChesscomCadence,
  FideCadence,
  LichessCadence,
  RatingsSnapshot,
} from './types';
import { ConsistencyAlerts } from './ConsistencyAlerts';
import { useFfeTournaments, type FfeTournament } from '@/api/ratings';

/**
 * VueGlobalePanel — onglet 1 « Vue globale » du StatsPage (Stats Hub V2 PR #J).
 *
 * Remplace `<RatingsSummaryCard>` historique en enrichissant la vue avec :
 *
 *  1. **Ratings actuels** — grid responsive 3 colonnes desktop / 1 colonne mobile
 *     avec FIDE (3 cards), Lichess (jusqu'à 7 cards selon ce que renvoie le snapshot),
 *     chess.com (4 cards).
 *  2. **Alertes consistency** — `<ConsistencyAlerts>` existant, écart > 200 ELO
 *     cross-platform.
 *  3. **Derniers événements** — dernier tournoi FFE + dernière partie (proxy maj
 *     ratings updated_at en l'absence d'endpoint dédié).
 *
 * Pourquoi enrichir et ne pas remplacer en silence : la spec design v2 (cf.
 * `docs/plans/2026-05-19-stats-hub-v2-design.md` §7) parle de "résumé 3 plateformes
 * + alertes + dernier tournoi FFE + dernière partie en ligne". On garde la voix
 * Léon DESIGN.md §6 (direct, technique, sans pathos) sur les empty states.
 */

const FIDE_ORDER: FideCadence[] = ['standard', 'rapide', 'blitz'];

// Côté snapshot Lichess (RatingsAggregator::LICHESS_CADENCES), 7 clés possibles
// dont `puzzle_rush` et `storm` en snake_case alors que `LichessCadence` côté
// front utilise camelCase (`puzzleRush`). On lookup les 7 clés via cast
// dynamique pour rester tolérant aux 2 conventions tant que ratings.ts n'a pas
// migré (sujet hors scope PR #J).
type LichessKey = LichessCadence | 'puzzle_rush' | 'storm';
const LICHESS_ORDER: LichessKey[] = [
  'classical',
  'rapid',
  'blitz',
  'bullet',
  'puzzle',
  'puzzleRush',
  'storm',
];

const CHESSCOM_ORDER: ChesscomCadence[] = ['rapid', 'blitz', 'bullet', 'tactics'];

const TIME_CONTROL_LABEL: Record<FfeTournament['time_control'], string> = {
  standard: 'Standard',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

interface VueGlobalePanelProps {
  snapshot: RatingsSnapshot;
}

export function VueGlobalePanel({ snapshot }: VueGlobalePanelProps): JSX.Element {
  // Tournois FFE : on tire les 5 plus récents pour la card "dernier tournoi".
  // On garde la requête volontairement large (limit 5) pour éviter un
  // re-fetch si Léon ouvre ensuite l'onglet FIDE et a besoin de la liste.
  const ffeQ = useFfeTournaments(undefined, 5);
  const lastTournament: FfeTournament | null = ffeQ.data?.tournaments[0] ?? null;

  // Dernière partie en ligne : on utilise comme proxy le `updated_at` le plus
  // récent parmi les cadences Lichess et chess.com (l'endpoint /games/recent
  // n'existe pas encore — décision pragmatique pour ne pas bloquer la PR #J).
  const lastOnlineUpdate = pickMostRecentOnlineUpdate(snapshot);

  return (
    <div className="flex flex-col gap-8">
      {/* Section 1 : Ratings actuels — grid 3 colonnes desktop / 1 col mobile */}
      <section aria-label="Ratings actuels">
        <h2 className="text-heading-2 text-paper-50 mb-4">Ratings actuels</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Bloc FIDE */}
          <div className="flex flex-col gap-3">
            <h3 className="text-caption uppercase tracking-widest font-mono text-ink-300">
              <span style={{ color: 'var(--paper-50)' }}>FIDE</span>
              <span className="ml-2 text-ink-300">officiel</span>
            </h3>
            <div className="flex flex-col gap-3">
              {FIDE_ORDER.map((cad) => {
                const r = snapshot.fide?.[cad] ?? null;
                return (
                  <RatingBadge
                    key={cad}
                    platform="fide"
                    cadence={cad}
                    value={r?.value ?? null}
                    updatedAt={r?.rated_at ?? null}
                    variant="card"
                  />
                );
              })}
            </div>
            {!snapshot.fide?.standard && !snapshot.fide?.rapide && !snapshot.fide?.blitz && (
              <p className="text-ink-300 text-body-sm italic mt-1">
                Aucun rating FIDE saisi. Onglet FIDE → ajoute ta première saisie.
              </p>
            )}
          </div>

          {/* Bloc Lichess */}
          <div className="flex flex-col gap-3">
            <h3 className="text-caption uppercase tracking-widest font-mono text-ink-300">
              <span style={{ color: 'var(--violet-500)' }}>Lichess</span>
              <span className="ml-2 text-ink-300">entraînement</span>
            </h3>
            <div className="flex flex-col gap-3">
              {LICHESS_ORDER.map((cad) => {
                const r = readLichessRating(snapshot, cad);
                // Pour les cadences extra (puzzle_rush, storm) on n'affiche
                // la card que si le snapshot la contient réellement, sinon on
                // pollue avec 7 lignes vides pour Léon. Cards "—" autorisées
                // pour les 5 cadences principales (cohérent FIDE/chess.com).
                if (
                  (cad === 'puzzle_rush' || cad === 'puzzleRush' || cad === 'storm') &&
                  r === null
                ) {
                  return null;
                }
                // Storm n'est pas exposé par `RatingBadge` (LichessCadence ne le
                // contient pas dans `ratings.ts`). On rend une card équivalente
                // localement plutôt que d'élargir le contrat global ici.
                if (cad === 'storm') {
                  return (
                    <LichessStormCard
                      key={cad}
                      value={r?.value ?? null}
                      updatedAt={r?.updated_at ?? null}
                    />
                  );
                }
                return (
                  <RatingBadge
                    key={cad}
                    platform="lichess"
                    cadence={normalizeLichessCadence(cad)}
                    value={r?.value ?? null}
                    updatedAt={r?.updated_at ?? null}
                    variant="card"
                  />
                );
              })}
            </div>
          </div>

          {/* Bloc chess.com */}
          <div className="flex flex-col gap-3">
            <h3 className="text-caption uppercase tracking-widest font-mono text-ink-300">
              <span style={{ color: 'var(--amber-500)' }}>chess.com</span>
              <span className="ml-2 text-ink-300">volume</span>
            </h3>
            <div className="flex flex-col gap-3">
              {CHESSCOM_ORDER.map((cad) => {
                const r = snapshot.chesscom?.[cad] ?? null;
                return (
                  <RatingBadge
                    key={cad}
                    platform="chesscom"
                    cadence={cad}
                    value={r?.value ?? null}
                    updatedAt={r?.updated_at ?? null}
                    variant="card"
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 : Alertes consistency cross-platform */}
      <section aria-label="Alertes consistency">
        <ConsistencyAlerts alerts={snapshot.consistency_check ?? []} />
      </section>

      {/* Section 3 : Derniers événements (2 cards côte à côte) */}
      <section aria-label="Derniers événements">
        <h2 className="text-heading-2 text-paper-50 mb-4">Derniers événements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LastFfeTournamentCard
            tournament={lastTournament}
            isLoading={ffeQ.isLoading}
            isError={ffeQ.isError}
          />
          <LastOnlineActivityCard updatedAt={lastOnlineUpdate} />
        </div>
      </section>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Sous-composants                                                          */
/* ----------------------------------------------------------------------- */

interface LastFfeTournamentCardProps {
  tournament: FfeTournament | null;
  isLoading: boolean;
  isError: boolean;
}

function LastFfeTournamentCard({
  tournament,
  isLoading,
  isError,
}: LastFfeTournamentCardProps): JSX.Element {
  return (
    <article
      data-testid="last-tournament-card"
      className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-3 min-h-[180px]"
    >
      <header className="flex items-baseline justify-between gap-2">
        <span
          className="text-caption uppercase tracking-widest font-mono"
          style={{ color: 'var(--paper-50)' }}
        >
          Dernier tournoi FFE
        </span>
      </header>

      {isLoading && (
        <p className="text-ink-300 font-mono text-body-sm">Chargement…</p>
      )}

      {!isLoading && isError && (
        <p className="text-body-sm text-coral-500">
          Impossible de charger les tournois FFE.
        </p>
      )}

      {!isLoading && !isError && tournament === null && (
        <p className="text-body-sm text-ink-300 italic leading-relaxed">
          Aucun tournoi détecté. Joue ton prochain open, on capte automatiquement.
        </p>
      )}

      {!isLoading && !isError && tournament !== null && (
        <div className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-paper-50 m-0 leading-tight">
            {tournament.name}
          </h3>
          <p className="text-body-sm text-ink-300 font-mono">
            <span className="tabular-nums">{tournament.started_at}</span>
            {tournament.location !== null && (
              <span className="ml-2">· {tournament.location}</span>
            )}
            <span className="ml-2">· {TIME_CONTROL_LABEL[tournament.time_control]}</span>
          </p>
          <div className="grid grid-cols-3 gap-2 mt-1 text-body-sm">
            <ScoreCell label="Score" value={formatScore(tournament.score, tournament.rounds_played)} />
            <ScoreCell label="Perf" value={tournament.perf !== null ? String(tournament.perf) : '—'} />
            <VariationCell value={tournament.elo_variation} />
          </div>
          {tournament.url !== null && tournament.url !== '' && (
            <a
              href={tournament.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 text-body-sm text-paper-50 hover:text-acid-500 transition-colors w-fit"
            >
              Voir la fiche FFE
              <ArrowSquareOut size={14} weight="regular" aria-hidden="true" />
            </a>
          )}
        </div>
      )}
    </article>
  );
}

interface LastOnlineActivityCardProps {
  updatedAt: string | null;
}

function LastOnlineActivityCard({ updatedAt }: LastOnlineActivityCardProps): JSX.Element {
  return (
    <article
      data-testid="last-online-card"
      className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-3 min-h-[180px]"
    >
      <header className="flex items-baseline justify-between gap-2">
        <span
          className="text-caption uppercase tracking-widest font-mono"
          style={{ color: 'var(--violet-500)' }}
        >
          Dernière partie en ligne
        </span>
      </header>

      {updatedAt === null ? (
        <p className="text-body-sm text-ink-300 italic leading-relaxed">
          Pas de partie récente détectée. Joue ta prochaine session, on capte automatiquement.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-body-sm text-paper-50 leading-relaxed">
            Tes ratings Lichess et chess.com ont été synchronisés sur ta dernière session
            d'entraînement.
          </p>
          <p className="text-body-sm font-mono text-ink-300 tabular-nums">
            maj {updatedAt}
          </p>
        </div>
      )}
    </article>
  );
}

interface LichessStormCardProps {
  value: number | null;
  updatedAt: string | null;
}

/**
 * LichessStormCard — card équivalent `<RatingBadge variant="card">` pour la
 * cadence Storm, non couverte par `RatingBadge` (Storm n'est pas dans
 * `LichessCadence` de `ratings.ts`). Pattern visuel identique pour cohérence.
 */
function LichessStormCard({ value, updatedAt }: LichessStormCardProps): JSX.Element {
  const displayValue = value == null ? '—' : String(Math.round(value));
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-violet-500" aria-hidden="true" />
        <span
          className="text-caption uppercase font-mono"
          style={{ color: 'var(--violet-500)', letterSpacing: '0.05em', fontWeight: 500 }}
        >
          Lichess · Storm
        </span>
      </div>
      <div className="font-mono tabular-nums" style={{ fontSize: 32, color: 'var(--paper-50)' }}>
        {displayValue}
      </div>
      {updatedAt !== null && updatedAt !== '' && (
        <div className="text-[11px] font-mono text-ink-300">maj {updatedAt}</div>
      )}
    </div>
  );
}

interface ScoreCellProps {
  label: string;
  value: string;
}

function ScoreCell({ label, value }: ScoreCellProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption uppercase tracking-widest text-ink-300">{label}</span>
      <span className="font-mono tabular-nums text-paper-50">{value}</span>
    </div>
  );
}

function VariationCell({ value }: { value: number | null }): JSX.Element {
  const display = value === null ? '—' : (value > 0 ? `+${value}` : String(value));
  const tone =
    value === null
      ? 'var(--ink-300)'
      : value > 0
        ? 'var(--volt-500)'
        : value < 0
          ? 'var(--coral-500)'
          : 'var(--ink-300)';
  const Icon = value === null || value === 0 ? Minus : value > 0 ? ArrowUp : ArrowDown;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption uppercase tracking-widest text-ink-300">Variation</span>
      <span
        className="font-mono tabular-nums flex items-center gap-1"
        style={{ color: tone }}
      >
        <Icon size={12} weight="bold" aria-hidden="true" />
        {display}
      </span>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Helpers purs                                                             */
/* ----------------------------------------------------------------------- */

function formatScore(score: number | null, rounds: number | null): string {
  if (score === null) return '—';
  const formatted = Number.isInteger(score) ? score.toFixed(1) : String(score);
  if (rounds === null || rounds <= 0) return formatted;
  return `${formatted} / ${rounds}`;
}

/**
 * Cherche la valeur la plus récente parmi tous les `updated_at` Lichess+chess.com
 * du snapshot. Utilisé comme proxy "dernière partie en ligne" en l'absence
 * d'endpoint dédié `/games/recent`.
 */
function pickMostRecentOnlineUpdate(snapshot: RatingsSnapshot): string | null {
  const candidates: string[] = [];
  for (const r of Object.values(snapshot.lichess ?? {})) {
    if (r !== null && r !== undefined && typeof r.updated_at === 'string' && r.updated_at !== '') {
      candidates.push(r.updated_at);
    }
  }
  for (const r of Object.values(snapshot.chesscom ?? {})) {
    if (r !== null && r !== undefined && typeof r.updated_at === 'string' && r.updated_at !== '') {
      candidates.push(r.updated_at);
    }
  }
  if (candidates.length === 0) return null;
  candidates.sort();
  return candidates[candidates.length - 1] ?? null;
}

/**
 * Lookup `snapshot.lichess[key]` en tolérant les 2 conventions camelCase
 * (`puzzleRush`) et snake_case (`puzzle_rush`, `storm`) — le backend
 * `RatingsAggregator` renvoie en snake_case tandis que le type frontend
 * historique est en camelCase. À refactor quand `ratings.ts` aura migré.
 */
function readLichessRating(
  snapshot: RatingsSnapshot,
  key: LichessKey,
): { value: number; updated_at: string | null } | null {
  const lichess = snapshot.lichess as Record<string, { value: number; updated_at: string | null } | null | undefined>;
  const r = lichess[key];
  if (r !== null && r !== undefined) return r;
  // fallback : si on cherche `puzzleRush` mais backend a renvoyé `puzzle_rush`
  if (key === 'puzzleRush' && lichess['puzzle_rush'] !== null && lichess['puzzle_rush'] !== undefined) {
    return lichess['puzzle_rush'];
  }
  if (key === 'puzzle_rush' && lichess['puzzleRush'] !== null && lichess['puzzleRush'] !== undefined) {
    return lichess['puzzleRush'];
  }
  return null;
}

/**
 * Normalise la clé Lichess pour la passer à `<RatingBadge>` (qui attend une
 * `LichessCadence` du type ratings.ts). `puzzle_rush`/`storm` deviennent
 * `puzzleRush`/`puzzle` faute de mieux côté UI (RatingBadge a `puzzleRush`
 * mais pas `storm`).
 */
function normalizeLichessCadence(key: LichessKey): LichessCadence {
  if (key === 'puzzle_rush') return 'puzzleRush';
  if (key === 'storm') return 'puzzleRush'; // affiche libellé "Puzzle Rush" pour Storm faute de label dédié
  return key as LichessCadence;
}
